import { groupBy, keyBy } from "lodash";
import type { ConditionalKeys } from "type-fest";

export type SingletonOrArray<G> = G | G[];

/*
 * Helper functions to correctly type the return types of Object.keys and Object.entries
 * now that typescript 4.4 supports type-branded strings/symbols in Records.
 */

type ObjectKey = string | number | symbol;

export const typedObjectKeys = <Obj extends object>(
  obj: Obj,
): Array<keyof Obj> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Object.keys(obj) as any;
};

export const typedObjectValues = <Obj extends object>(
  obj: Obj,
): Array<Obj[keyof Obj]> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Object.values(obj) as any;
};

export const typedObjectEntries = <Obj extends object>(
  obj: Obj,
): { [K in keyof Obj]: [K, Obj[K]] }[keyof Obj][] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Object.entries(obj) as any;
};

export const typedObjectFromEntries = <
  Entries extends readonly (readonly [ObjectKey, unknown])[],
>(
  entries: Entries | Iterable<Entries[number]>,
): {
  [Tup in Entries[number] as Tup[0]]: Tup[1];
} => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Object.fromEntries(entries) as any;
};

export const typedRecordFromEntries = <Key extends string, T>(
  entries: Iterable<readonly [Key, T]>,
): Record<Key, T> => {
  return Object.fromEntries(entries) as Record<Key, T>;
};

export const typedGetOwnPropertyNames = <Obj extends object>(
  obj: Obj,
): Array<keyof Obj> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- needed to correctly type property names as keys
  return Object.getOwnPropertyNames(obj) as any;
};

export const typedKeyGroupBy = <
  Obj,
  K extends ConditionalKeys<Obj, string | number | symbol>,
>(
  collection: readonly Obj[],
  key: K,
): Obj[K] extends string | number | symbol
  ? {
      // Adding in the optional operator is correct -- if the value doesn't appear in the data at runtime,
      // then it won't be a key of the object -- but because our tsconfig doesn't have `exactOptionalPropertyTypes: true`,
      // it means that the value is also set to `| undefined` (even though that's not accurate). Unfortunately, there's
      // no workaround. Consumers will have to handle this case.
      [V in Obj[K]]?: Extract<Obj, Record<K, V>>[];
    }
  : never => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return groupBy(collection, key) as any;
};

export const typedGroupBy = <Obj, K extends string>(
  collection: readonly Obj[],
  key: (item: Obj) => K,
): Partial<Record<K, Obj[]>> => {
  return groupBy(collection, key) as Record<K, Obj[]>;
};

export const typedKeyBy = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Obj extends Record<K, any>,
  K extends keyof Obj,
>(
  collection: readonly Obj[],
  key: K,
): Record<Obj[K], Obj> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return keyBy(collection, key) as any;
};
